// @ts-nocheck
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { GoogleMap, useJsApiLoader, Libraries } from "@react-google-maps/api";
import "./App.css";
import "./DetailsCard.css"; // Import for horizontal controls styling
import DetailsCard from "./DetailsCard";
import SearchField from "./SearchField";
import useAudioNarration from "./useAudioNarration.js";
import QuotaModal from "./QuotaModal";
import SalesPromptModal from './SalesPromptModal';
import SmartSignIn from './components/SmartSignIn';
import MainMenu, { LANGUAGE_OPTIONS } from './MainMenu';
import UserHistoryPopup from './UserHistoryPopup';
import EnvironmentBanner from './components/EnvironmentBanner';
import usePWAInstall from './hooks/usePWAInstall';
import IOSPWAInstallManual from './components/IOSPWAInstallManual';
import AndroidPWAInstallManual from './components/AndroidPWAInstallManual';
import { logLocationVisit, logArticleVisit } from './services/historyLogger';
import AdvancedSvgMarker from './components/AdvancedSvgMarker';
import AdvancedImageMarker from './components/AdvancedImageMarker';
import WelcomeScreen from './components/WelcomeScreen';
import { fetchGeocodingNominatim, fetchTopicWikidataFromNominatim, getWikiLanguageFromNominatim, isLatLngWithinBounds, resolveDisplayedPlaceWikidata } from './services/geocodingService';
import { MARKER_SCALE_FACTOR, THUMB_DISPLAY_SIZE } from './utils/markerUtils';
import { mapComputeWorkerManager } from './utils/mapComputeWorkerManager';
import { useGPSTracking, GPS_STATUS, isValidCoordinate, isValidLatLng } from './hooks/useGPSTracking';
import { activityTracker } from './utils/activityTracker';
import { extractTopicGeoContext, attachTopicGeoContext } from './utils/topicGeoContext';
import { setDisplayLocation, setMapZoom as setMapZoomSignal, useDerivedLocationName, useDerivedLocationTopicMeta, useDisplayLocation, attachDerivedTopicSelection, deriveTopicFromAddressByZoom } from './signals/displayLocation';
import DetailsCardV2 from './features/detailsCardV2/DetailsCardV2';
import { useTopicStore } from './features/detailsCardV2/store';
import ThinkingIndicator from './components/ThinkingIndicator';
import { cleanupStartupUrlParameters, getPredefinedStartupLocation } from './app/controller';
import { dismissWelcomeScreen, setTopSightsList, setUserLanguageCode, useAppField, useAppStore } from './app/store';
import { useDevCliBridge } from './app/hooks/useDevCliBridge';
import { useUserEventEffects } from './app/hooks/useUserEventEffects';
import { USER_GEMINI_API_KEY_STORAGE_KEY } from './packages/audio-narration-core';
import {
  loginMachine,
  selectCanUseAuthenticatedRequests,
  selectIsFullAccount,
  selectIsGuestAccount,
  useLoginStore,
} from './auth/loginMachine';

const USE_DETAILS_CARD_V2 = true;

// Language selector options - moved to MainMenu component

// Helper for logging
const log = (...args) => console.log("[OSM-Wiki]", ...args);

// Wikipedia API constants
const WIKI_BASE_ENDPOINT = "https://{lang}.wikipedia.org/w/api.php";
const WIKI_ORIGIN_PARAM = "origin=*"; // required for CORS
// Separate resolution from display size to get higher quality thumbnails without changing marker size
const DEFAULT_THUMB_RESOLUTION = 180; // px – default resolution for API requests (doubled from 100)
const TOPSIGHT_DISPLAY_SIZE = 85; // px – display size for top sights (smaller than regular articles)
const MAX_ARTICLES = 26;

// Geocoding throttle
const GEOCODING_THROTTLE_MS = 1000; // 1 second throttle
const MAX_CACHED_TOPIC_CENTER_DISTANCE_KM = 5;

const normalizeTopicCacheText = (value) => String(value || '').trim().toLowerCase();

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getCachedTopicBoundsHit(locationData, lat, lng, currentZoom) {
  const topicBounds = locationData?.topic_bounds;
  if (!topicBounds) return null;

  const topicAtCurrentZoom = deriveTopicFromAddressByZoom(locationData?.address || {}, currentZoom);
  const currentTopicName = topicAtCurrentZoom?.topicName || '';
  const currentAdminLevel = topicAtCurrentZoom?.adminLevel || '';
  const boundsTopicNames = [
    topicBounds.placeName,
    topicBounds.requestedPlaceName,
    locationData?.topic_place_name,
  ].filter(Boolean);
  const boundsAdminLevel = topicBounds.topicAdminLevel || locationData?.topic_admin_level || '';

  if (!currentTopicName || !currentAdminLevel || boundsTopicNames.length === 0 || !boundsAdminLevel) return null;
  if (!boundsTopicNames.some((name) => normalizeTopicCacheText(currentTopicName) === normalizeTopicCacheText(name))) return null;
  if (normalizeTopicCacheText(currentAdminLevel) !== normalizeTopicCacheText(boundsAdminLevel)) return null;
  if (!isLatLngWithinBounds(lat, lng, topicBounds)) return null;

  const centerLat = Number(topicBounds.centerLat ?? locationData?.lat);
  const centerLon = Number(topicBounds.centerLon ?? locationData?.lon ?? locationData?.lng);
  if (!Number.isFinite(centerLat) || !Number.isFinite(centerLon)) {
    return null;
  }

  const distanceFromTopicCenterKm = haversineKm(Number(lat), Number(lng), centerLat, centerLon);
  if (!Number.isFinite(distanceFromTopicCenterKm) || distanceFromTopicCenterKm > MAX_CACHED_TOPIC_CENTER_DISTANCE_KM) {
    return null;
  }

  return {
    topicName: currentTopicName,
    adminLevel: currentAdminLevel,
    bounds: topicBounds,
    distanceFromTopicCenterKm,
  };
}

function unpackTopicResolution(topicResolution) {
  return {
    qid: typeof topicResolution === 'string'
      ? topicResolution
      : topicResolution?.qid || null,
    topicBounds: typeof topicResolution === 'object'
      ? topicResolution?.topicBounds || null
      : null,
  };
}

function mergeTopicResolutionIntoLocation(prev, topicResolution, fallbackPlaceName, fallbackAdminLevel) {
  if (!prev) return prev;

  const { qid, topicBounds } = unpackTopicResolution(topicResolution);
  if (!qid && !topicBounds) return prev;

  return {
    ...prev,
    topic_place_name: fallbackPlaceName || prev.topic_place_name || topicBounds?.placeName || '',
    topic_admin_level: prev.topic_admin_level || fallbackAdminLevel || topicBounds?.topicAdminLevel || '',
    ...(qid ? {
      topic_wikidata: qid,
      ...(prev.topic_wikidata !== qid ? { topic_population: null } : {}),
    } : {}),
    ...(topicBounds ? { topic_bounds: topicBounds } : {}),
  };
}

// Google Maps defaults
const DEFAULT_CENTER = { lat: 51.1657, lng: 10.4515 }; // Center of Europe
const MAP_CONTAINER_STYLE = { width: "100vw", height: "calc(100vh - 70px)" }; // Account for bottom controls

// NEW: Default when GPS is unavailable – zoomed-out center of Europe
const NO_GPS_CENTER = { lat: 51.1657, lng: 10.4515 }; // Germany (rough centre of Europe)

const LAST_MAP_VIEW_KEY = 'lastMapView';
const LAST_MAP_VIEW_SAVE_INTERVAL_MS = 5000;

// For default articles without thumbnail: use simple dot
const FALLBACK_THUMBNAIL = "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Commons-logo.svg/80px-Commons-logo.svg.png";
const DOT_FILL = "#ADD8E6"; // pale blue
const DOT_SCALE = 5 * MARKER_SCALE_FACTOR; // 3.75

/* -----------------------------------------------------------
 * Visual highlight for the *selected* article marker
 * --------------------------------------------------------- */
const SELECTED_RING_COLOR = '#24C94A';   // bright green
const SELECTED_RING_WIDTH = 3;           // px
// Scale calculation based on original logic (THUMB_DISPLAY_SIZE * 0.8 / 2 + 8) scaled by factor
const SELECTED_RING_SCALE = (THUMB_DISPLAY_SIZE * 0.8 / 2 + 8) * MARKER_SCALE_FACTOR; // (100 * 0.8 / 2 + 8) * 0.75 = (40 + 8) * 0.75 = 48 * 0.75 = 36

// Original on-screen diameter of icon (based on THUMB_DISPLAY_SIZE)
const ORIGINAL_TOTAL_SIZE = ((THUMB_DISPLAY_SIZE * 0.8) + 2 * 4 * 0.8) * MARKER_SCALE_FACTOR; // ≈66px on screen

// Top sight thumbnail size used in legacy code (no scale factor)
const TOPSIGHT_ICON_SIZE = TOPSIGHT_DISPLAY_SIZE * 0.8 + 2 * 3 * 0.8; // ~64.8px (smaller than regular articles)
const HIDDEN_TOPSIGHT_STAR_SIZE = 28;

// Maximum characters for on-map labels
const LABEL_MAX_CHARS = 21;
const EMPTY_TOP_SIGHTS = Object.freeze([]);
const TOPSIGHT_OVERLAP_PADDING = 12;
const TOPSIGHT_LABEL_MIN_ZOOM = 14;
const ARTICLE_LABEL_MIN_ZOOM = 19;
const BLUE_DOT_MARKER_MAX_ZOOM = 15;

const truncateLabel = (str) => (str.length > LABEL_MAX_CHARS ? `${str.slice(0, LABEL_MAX_CHARS)}…` : str);

const TOPSIGHT_VIEW_FIELDS = Object.freeze([
  'pvSum',
  'wikiViews',
  'wikiviews',
  'pageviews',
  'pageViews',
  'viewCount',
  'views',
  'monthlyViews',
  'dailyViews',
]);

function toFiniteNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function readTopSightWikiViews(sight) {
  for (const field of TOPSIGHT_VIEW_FIELDS) {
    const directValue = toFiniteNumber(sight?.[field]);
    if (directValue != null) return directValue;
  }

  const pageviews = sight?.pageviews || sight?.pageViews || sight?.wikiPageviews;
  if (pageviews && typeof pageviews === 'object') {
    for (const field of ['total', 'sum', 'views', 'count']) {
      const nestedValue = toFiniteNumber(pageviews[field]);
      if (nestedValue != null) return nestedValue;
    }
  }

  return null;
}

function getTopSightProminence(sight, index, totalCount) {
  const wikiViews = readTopSightWikiViews(sight);
  if (wikiViews != null) return wikiViews;

  const articleSize = toFiniteNumber(sight?.articleSizeBytes);
  if (articleSize != null) return articleSize;

  return Math.max(0, totalCount - index);
}

function getTopSightMarkerId(sight, index) {
  return String(sight?.id || sight?.link || index);
}

// Placeholder topic when no GPS is available
const MOVE_MAP_PROMPT_ARTICLE = {
  pageid: 'prompt',
  title: 'Move the map to your area of interest',
  lat: null,
  lon: null,
  thumbnail: FALLBACK_THUMBNAIL,
  fullurl: null,
  pvSum: 0,
};

// Add debounce utility
function debounce(func, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

/*********************
* Main App Component
*********************/
export default function AppView() {
  const [articles] = useAppField('articles');
  const [selectedArticle, setSelectedArticle] = useAppField('selectedArticle');
  const [focusedTopSightMarkerId, setFocusedTopSightMarkerId] = useAppField('focusedTopSightMarkerId');
  const [viewportWikiLanguage, setViewportWikiLanguage] = useAppField('viewportWikiLanguage');
  const [userLanguage] = useAppField('userLanguage');
  const [userLanguageLabel] = useAppField('userLanguageLabel');

  // Get data from signals early to avoid use-before-define errors
  const cityName = useDerivedLocationName();
  const locationTopicMeta = useDerivedLocationTopicMeta();
  const nominatimData = useDisplayLocation();
  const activeLocationTopSights = useTopicStore((s) => {
    const active = s.activeTopicId ? s.topics[s.activeTopicId] : null;
    if (active && active.type === 'location') {
      return active.topSightsImages?.items ?? EMPTY_TOP_SIGHTS;
    }

    const suggested = s.suggestedTopicId ? s.topics[s.suggestedTopicId] : null;
    if (suggested && suggested.type === 'location') {
      return suggested.topSightsImages?.items ?? EMPTY_TOP_SIGHTS;
    }

    return EMPTY_TOP_SIGHTS;
  });
  // nominatimData moved to signal - remove useState
  const [isMenuOpen, setIsMenuOpen] = useAppField('isMenuOpen');
  const showSalesPrompt = useAppStore((state) => state.showSalesPrompt);
  const setShowSalesPrompt = useAppStore((state) => state.setShowSalesPrompt);
  const showQuotaModal = useAppStore((state) => state.showQuotaModal);
  const setShowQuotaModal = useAppStore((state) => state.setShowQuotaModal);
  const [showIOSInstallManual, setShowIOSInstallManual] = useAppField('showIOSInstallManual');
  const [showAndroidInstallManual, setShowAndroidInstallManual] = useAppField('showAndroidInstallManual');
  const showSuccessNotification = useAppStore((state) => state.showSuccessNotification);
  const setShowSuccessNotification = useAppStore((state) => state.setShowSuccessNotification);
  const showSigninSuccessBanner = useAppStore((state) => state.showSigninSuccessBanner);
  const setShowSigninSuccessBanner = useAppStore((state) => state.setShowSigninSuccessBanner);
  const [showUserHistory, setShowUserHistory] = useAppField('showUserHistory');
  const [isMapCenteredOnUser, setIsMapCenteredOnUser] = useAppField('isMapCenteredOnUser');
  const [autoShowLocationSights, setAutoShowLocationSights] = useAppField('autoShowLocationSights');
  const [topSights] = useAppField('topSights');
  const mapTopSights = USE_DETAILS_CARD_V2 ? activeLocationTopSights : topSights;
  const [articleIcons] = useAppField('articleIcons');
  const [topSightIcons] = useAppField('topSightIcons');
  const [displayedArticles] = useAppField('displayedArticles');
  const [history, setHistory] = useAppField('history');
  const [historyIndex, setHistoryIndex] = useAppField('historyIndex');
  const [mapTypeId, setMapTypeId] = useAppField('mapTypeId');
  const [detailsCardHeightPx, setDetailsCardHeightPx] = useAppField('detailsCardHeightPx');
  const [isDesktopUI] = useAppField('isDesktopUI');

  // Track search field usage for topsights requests
  const [isFromSearchfield, setIsFromSearchfield] = useAppField('isFromSearchfield');
  const [searchFieldLocationString, setSearchFieldLocationString] = useAppField('searchFieldLocationString');
  const [searchFieldRequestRequired, setSearchFieldRequestRequired] = useAppField('searchFieldRequestRequired');
  const [showWelcomeScreen] = useAppField('showWelcomeScreen');

  const handleWelcomeScreenClose = () => {
    dismissWelcomeScreen();
  };

  // Handle language change with persistence
  const handleLanguageChange = (newLanguage) => {
    setUserLanguageCode(newLanguage);
    localStorage.setItem('userLanguage', newLanguage);
  };

  const [boundsSnapshot, setBoundsSnapshot] = useAppField('boundsSnapshot');
  const historyIndexRef = useRef(-1);
  const [lastGeocodingCall, setLastGeocodingCall] = useAppField('lastGeocodingCall');
  const [initialLocationSet, setInitialLocationSet] = useAppField('initialLocationSet');
  const [initialNominatimTopicSet, setInitialNominatimTopicSet] = useAppField('initialNominatimTopicSet');
  const selectedStartupLocation = useAppStore((state) => state.selectedStartupLocation);
  const [landingStartupLocation, setLandingStartupLocation] = useState(null);
  const activeStartupLocation = landingStartupLocation || selectedStartupLocation;
  const [initialMapConfigured, setInitialMapConfigured] = useAppField('initialMapConfigured');
  const [showTooltip, setShowTooltip] = useAppField('showTooltip');
  const detailsCardRef = useRef(null);
  const pendingStartupActivationRef = useRef(null);
  const startupTopicActivationInFlightRef = useRef(false);

  // Track current map zoom so we can toggle article labels at high zoom levels
  const [mapZoom, setMapZoom] = useAppField('mapZoom');

  // const isLocalhost = typeof window !== 'undefined' &&
  //   ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  // const [photonAddressLine, setPhotonAddressLine] = useState('');
  // const [photonCenter, setPhotonCenter] = useState(null);

  // Debug geocoding comparison window disabled.
  // useEffect(() => {
  //   if (!isLocalhost) return undefined;
  //
  //   const lat = Number(photonCenter?.lat);
  //   const lon = Number(photonCenter?.lon);
  //
  //   if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
  //     setPhotonAddressLine('');
  //     return undefined;
  //   }
  //
  //   const controller = new AbortController();
  //
  //   (async () => {
  //     try {
  //       const url = `https://photon.komoot.io/reverse?lon=${lon}&lat=${lat}`;
  //       const response = await fetch(url, { signal: controller.signal });
  //       if (!response.ok) {
  //         throw new Error(`Photon reverse failed: ${response.status}`);
  //       }
  //
  //       const data = await response.json();
  //       const p = data?.features?.[0]?.properties || {};
  //       const parts = [p.name, p.city, p.state, p.country].filter(Boolean);
  //       const line = parts.join(', ') || p.osm_value || 'No Photon match';
  //       setPhotonAddressLine(line);
  //     } catch (error) {
  //       if (error.name === 'AbortError') return;
  //       console.warn('[App] Photon reverse geocoding failed:', error);
  //       setPhotonAddressLine('Photon reverse failed');
  //     }
  //   })();
  //
  //   return () => controller.abort();
  // }, [isLocalhost, photonCenter?.lat, photonCenter?.lon]);

  // --------------------- User identity flow ---------------------
  const account = useLoginStore((state) => state.account);
  const isGuestAccount = selectIsGuestAccount(account);
  const isFullAccount = selectIsFullAccount(account);
  const canUseAuthenticatedRequests = selectCanUseAuthenticatedRequests(account);
  const [hasUserGeminiApiKey, setHasUserGeminiApiKey] = useState(() => {
    try {
      return !!String(localStorage.getItem(USER_GEMINI_API_KEY_STORAGE_KEY) || '').trim();
    } catch (_) {
      return false;
    }
  });
  const canUseAudioNarration = canUseAuthenticatedRequests || hasUserGeminiApiKey;

  useEffect(() => {
    const refreshUserGeminiApiKeyState = () => {
      try {
        setHasUserGeminiApiKey(!!String(localStorage.getItem(USER_GEMINI_API_KEY_STORAGE_KEY) || '').trim());
      } catch (_) {
        setHasUserGeminiApiKey(false);
      }
    };

    window.addEventListener('userGeminiApiKeyChanged', refreshUserGeminiApiKeyState);
    window.addEventListener('storage', refreshUserGeminiApiKeyState);

    return () => {
      window.removeEventListener('userGeminiApiKeyChanged', refreshUserGeminiApiKeyState);
      window.removeEventListener('storage', refreshUserGeminiApiKeyState);
    };
  }, []);

  // --------------------- PWA Installation flow ---------------------
  const pwaInstall = usePWAInstall();
  const showPwaInstallButton = !pwaInstall.isInstalled && !pwaInstall.isInstalledButInBrowser;

  useUserEventEffects({
    isFullAccount,
    setShowSuccessNotification,
    setShowSalesPrompt,
    setShowSigninSuccessBanner,
  });

  // Global audio hook: uses browser navigator.language by default for audio endpoint
  const narration = useAudioNarration(userLanguage);

  const userPreferredWikiLangCode = useMemo(() => {
    const selectedLangOption = LANGUAGE_OPTIONS.find(opt => opt.code === userLanguage);
    return selectedLangOption ? selectedLangOption.wikiLangCode : 'en'; // Default to 'en'
  }, [userLanguage]);

  const libraries = useMemo<Libraries>(() => ['places'], []);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const mapGestureCleanupRef = useRef(null);
  const userHasMovedMapRef = useRef(false);
  const [markerVisualScale, setMarkerVisualScale] = useState(1);
  const topSightMarkerVisualScale = useMemo(() => (
    Math.min(1, Math.max(0.48, Math.sqrt(markerVisualScale)))
  ), [markerVisualScale]);

  const hiddenTopSightMarkerIds = useMemo(() => {
    if (typeof window === 'undefined') return new Set();
    if (!isLoaded) return new Set();
    // Bounds act as the map-idle refresh signal for pan/viewport changes.
    void boundsSnapshot;

    const map = mapRef.current;
    const googleMaps = window.google?.maps;
    const projection = map?.getProjection?.();
    const zoom = Number(map?.getZoom?.() ?? mapZoom);

    if (!map || !googleMaps?.LatLng || !projection || !Number.isFinite(zoom)) {
      return new Set();
    }

    const scale = 2 ** zoom;
    const candidates = mapTopSights
      .filter((s) => s.id && Number.isFinite(s.lat) && Number.isFinite(s.lon))
      .map((s, idx) => {
        const worldPoint = projection.fromLatLngToPoint(new googleMaps.LatLng(s.lat, s.lon));
        const markerId = getTopSightMarkerId(s, idx);
        const markerSize = s.id && topSightIcons[s.id] ? TOPSIGHT_ICON_SIZE : 28;

        return {
          id: markerId,
          selected: selectedArticle?.pageid === s.id || focusedTopSightMarkerId === markerId,
          prominence: getTopSightProminence(s, idx, mapTopSights.length),
          originalIndex: idx,
          x: worldPoint.x * scale,
          y: worldPoint.y * scale,
          radius: ((markerSize * 0.9) * topSightMarkerVisualScale) / 2,
        };
      });

    const visibleFootprints = [];
    const hiddenIds = new Set();

    candidates
      .sort((a, b) => {
        if (a.selected !== b.selected) return a.selected ? -1 : 1;
        if (b.prominence !== a.prominence) return b.prominence - a.prominence;
        return a.originalIndex - b.originalIndex;
      })
      .forEach((candidate) => {
        const overlapsVisibleMarker = visibleFootprints.some((visible) => (
          Math.hypot(candidate.x - visible.x, candidate.y - visible.y)
            <= candidate.radius + visible.radius + TOPSIGHT_OVERLAP_PADDING
        ));

        if (overlapsVisibleMarker) {
          hiddenIds.add(candidate.id);
          return;
        }

        visibleFootprints.push(candidate);
      });

    return hiddenIds;
  }, [
    boundsSnapshot,
    focusedTopSightMarkerId,
    isLoaded,
    mapTopSights,
    mapZoom,
    topSightMarkerVisualScale,
    selectedArticle?.pageid,
    topSightIcons,
  ]);

  const hiddenArticleMarkerIds = useMemo(() => {
    if (typeof window === 'undefined') return new Set();
    if (!isLoaded) return new Set();
    // Bounds act as the map-idle refresh signal for pan/viewport changes.
    void boundsSnapshot;

    const map = mapRef.current;
    const googleMaps = window.google?.maps;
    const projection = map?.getProjection?.();
    const zoom = Number(map?.getZoom?.() ?? mapZoom);

    if (!map || !googleMaps?.LatLng || !projection || !Number.isFinite(zoom)) {
      return new Set();
    }

    const scale = 2 ** zoom;
    const topSightFootprints = mapTopSights
      .filter((s) => s.id && Number.isFinite(s.lat) && Number.isFinite(s.lon))
      .map((s, idx) => {
        const markerId = getTopSightMarkerId(s, idx);
        const isHiddenTopSight = hiddenTopSightMarkerIds.has(markerId);

        const worldPoint = projection.fromLatLngToPoint(new googleMaps.LatLng(s.lat, s.lon));
        const markerSize = isHiddenTopSight
          ? HIDDEN_TOPSIGHT_STAR_SIZE
          : (s.id && topSightIcons[s.id] ? TOPSIGHT_ICON_SIZE : 28);

        return {
          x: worldPoint.x * scale,
          y: worldPoint.y * scale,
          radius: ((markerSize * 0.9) * topSightMarkerVisualScale) / 2,
        };
      })
      .filter(Boolean);

    if (topSightFootprints.length === 0) return new Set();

    const hiddenIds = new Set();
    const articleBaseSize = ORIGINAL_TOTAL_SIZE;
    const fallbackSize = articleBaseSize * 0.27;

    articles.forEach((art) => {
      if (!art?.pageid || !Number.isFinite(art.lat) || !Number.isFinite(art.lon)) return;

      const worldPoint = projection.fromLatLngToPoint(new googleMaps.LatLng(art.lat, art.lon));
      const markerSize = art.thumbnail === FALLBACK_THUMBNAIL
        ? fallbackSize
        : (art.pvSum > 5000 ? articleBaseSize : articleBaseSize * 0.75);
      const articleRadius = ((markerSize * 0.9) * markerVisualScale) / 2;
      const articleX = worldPoint.x * scale;
      const articleY = worldPoint.y * scale;

      const touchesTopSight = topSightFootprints.some((topSight) => (
        Math.hypot(articleX - topSight.x, articleY - topSight.y) <= articleRadius + topSight.radius
      ));

      if (touchesTopSight) {
        hiddenIds.add(art.pageid);
      }
    });

    return hiddenIds;
  }, [articles, boundsSnapshot, hiddenTopSightMarkerIds, isLoaded, mapTopSights, mapZoom, markerVisualScale, topSightMarkerVisualScale, topSightIcons]);

  // Debouncing for marker scaling to prevent performance issues
  const [markerScaleTimeout, setMarkerScaleTimeout] = useState(null);

  const [lastZoomLevel, setLastZoomLevel] = useAppField('lastZoomLevel');

  const markMapConfiguredByUser = useCallback(() => {
    userHasMovedMapRef.current = true;
    setIsMapCenteredOnUser(false);

    if (!useAppStore.getState().initialMapConfigured) {
      setInitialMapConfigured(true);
    }
  }, [setInitialMapConfigured, setIsMapCenteredOnUser]);

  const attachMapGestureListeners = useCallback((mapInstance) => {
    if (mapGestureCleanupRef.current) {
      mapGestureCleanupRef.current();
      mapGestureCleanupRef.current = null;
    }

    const mapDiv = typeof mapInstance?.getDiv === 'function' ? mapInstance.getDiv() : null;
    if (!mapDiv) return;

    const listenerOptions = { passive: true, capture: true };
    const markGesture = () => markMapConfiguredByUser();
    const events = ['wheel', 'pointerdown', 'touchstart'];

    events.forEach((eventName) => {
      mapDiv.addEventListener(eventName, markGesture, listenerOptions);
    });

    mapGestureCleanupRef.current = () => {
      events.forEach((eventName) => {
        mapDiv.removeEventListener(eventName, markGesture, listenerOptions);
      });
    };
  }, [markMapConfiguredByUser]);

  // GPS tracking hook
  const shouldRequestGPS = !activeStartupLocation || activeStartupLocation === 'gps';
  const gps = useGPSTracking(shouldRequestGPS, (newPosition) => {
    console.log('[App] GPS position update received');
  });

  // Function to centralize setting the map to user position
  const centerMapOnUser = useCallback(() => {
    if (gps.position && mapRef.current && isValidCoordinate(gps.position)) {
      try {
        mapRef.current.panTo(gps.position);
        setIsMapCenteredOnUser(true);
      } catch (error) {
        console.error('[Map] Error centering on user:', error);
      }
    }
  }, [gps.position]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const applyLandingSelection = (rawValue) => {
      const startupLocation = getPredefinedStartupLocation(rawValue);
      if (!startupLocation) return;

      console.log('[Startup] Landing page selected location:', rawValue, startupLocation);
      setLandingStartupLocation(startupLocation);
      setInitialLocationSet(false);
      setInitialNominatimTopicSet(false);
      setSelectedArticle(null);
      setLastGeocodingCall(0);
      dismissWelcomeScreen();
    };

    const params = new URLSearchParams(window.location.search);
    applyLandingSelection(window.__CURIO_PENDING_SELECTED_LOCATION || params.get('selectedLocation'));

    const handleLandingLocationSelected = (event) => {
      applyLandingSelection(event?.detail?.selectedLocation);
    };

    window.addEventListener('curio:landing-location-selected', handleLandingLocationSelected);
    return () => {
      window.removeEventListener('curio:landing-location-selected', handleLandingLocationSelected);
    };
  }, [
    setInitialLocationSet,
    setInitialNominatimTopicSet,
    setLastGeocodingCall,
    setSelectedArticle,
  ]);

  const handleBoundsUpdate = useCallback((b) => setBoundsSnapshot(b), []);

  const topicPopulationCacheRef = useRef(new Map());
  const topicPopulationInFlightRef = useRef(new Map());

  const prefetchTopicPopulation = useCallback(async (qid) => {
    const cleanQid = String(qid || '').trim();
    if (!cleanQid) return null;

    if (topicPopulationCacheRef.current.has(cleanQid)) {
      const cached = topicPopulationCacheRef.current.get(cleanQid);
      log('[wikitag][Population] Cache hit for topic population', { qid: cleanQid, population: cached });
      return cached;
    }

    if (topicPopulationInFlightRef.current.has(cleanQid)) {
      log('[wikitag][Population] Reusing in-flight topic population request', { qid: cleanQid });
      return topicPopulationInFlightRef.current.get(cleanQid);
    }

    const requestPromise = (async () => {
      const params = new URLSearchParams({
        action: 'wbgetentities',
        format: 'json',
        ids: cleanQid,
        props: 'claims',
        origin: '*',
      });

      const url = `https://www.wikidata.org/w/api.php?${params.toString()}`;
      log('[wikitag][Population] Fetching topic population from Wikidata', { qid: cleanQid });

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Wikidata population fetch failed: ${response.status}`);
      }

      const data = await response.json();
      const claims = data?.entities?.[cleanQid]?.claims?.P1082 || [];
      const values = claims
        .map((claim) => claim?.mainsnak?.datavalue?.value?.amount)
        .map((amount) => Number(String(amount ?? '').replace('+', '')))
        .filter((num) => Number.isFinite(num) && num > 0);

      const population = values.length ? Math.max(...values) : null;
      topicPopulationCacheRef.current.set(cleanQid, population);
      log('[wikitag][Population] Topic population fetch completed', { qid: cleanQid, population });
      return population;
    })();

    topicPopulationInFlightRef.current.set(cleanQid, requestPromise);

    try {
      return await requestPromise;
    } finally {
      topicPopulationInFlightRef.current.delete(cleanQid);
    }
  }, []);

  const onLoad = useCallback(
    (mapInstance) => {
      mapRef.current = mapInstance;
      attachMapGestureListeners(mapInstance);
      // Initialize the geocoder once the map is loaded
      if (window.google && window.google.maps) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      handleBoundsUpdate(mapInstance.getBounds());
      const initialZoom = mapInstance.getZoom?.();
      if (Number.isFinite(initialZoom)) {
        setMapZoom(initialZoom);
        setMapZoomSignal(initialZoom);
      }
      // If userPosition is already available when map loads, center on it
      // only when startup is GPS-driven (not a persisted/custom startup view).
      if (gps.position && !initialLocationSet && (!activeStartupLocation || activeStartupLocation === 'gps')) {
        centerMapOnUser(); // Use the centralized function
        setInitialLocationSet(true);
      }
      // Add dragend listener to set map center after drag ends

    },
    [attachMapGestureListeners, handleBoundsUpdate, gps.position, initialLocationSet, centerMapOnUser, activeStartupLocation, setMapZoom]
  );

  // Handle map clicks, including native Google Maps POI labels/text.
  // When a map label provides a placeId, switch DetailsCard to that location topic.
  const handleMapClick = useCallback((event) => {
    activityTracker.updateLastActivity();
    markMapConfiguredByUser();

    const placeId = event?.placeId;
    const clickLat = event?.latLng?.lat ? event.latLng.lat() : null;
    const clickLng = event?.latLng?.lng ? event.latLng.lng() : null;

    if (!placeId || !mapRef.current || !window.google?.maps?.places) {
      return;
    }

    // Prevent default Google place info popup.
    if (typeof event.stop === 'function') {
      event.stop();
    }

    const placesService = new window.google.maps.places.PlacesService(mapRef.current);
    const request = {
      placeId,
      fields: ['name', 'formatted_address', 'geometry', 'address_components'],
    };

    placesService.getDetails(request, (place, status) => {
      const ok = status === window.google.maps.places.PlacesServiceStatus.OK && place;
      const lat = ok ? (place.geometry?.location?.lat?.() ?? clickLat) : clickLat;
      const lon = ok ? (place.geometry?.location?.lng?.() ?? clickLng) : clickLng;

      if (!isValidLatLng(lat, lon)) {
        console.warn('[Map] Place click returned invalid coordinates:', { placeId, lat, lon, status });
        return;
      }

      const components = ok ? (place.address_components || []) : [];
      const getLong = (type) => components.find((c) => c.types?.includes(type))?.long_name || '';
      const getShort = (type) => components.find((c) => c.types?.includes(type))?.short_name || '';

      const cityLike =
        getLong('locality') ||
        getLong('postal_town') ||
        getLong('administrative_area_level_2') ||
        getLong('administrative_area_level_1') ||
        (ok ? place.name : '');

      const clickedTopicAdminLevel = getLong('locality')
        ? 'city'
        : (getLong('postal_town')
          ? 'town'
          : (getLong('administrative_area_level_2')
            ? 'county'
            : (getLong('administrative_area_level_1') ? 'state' : 'city')));

      // Use the EXACT clicked map label/name as topic title when available.
      const clickedTopicName = (ok ? place.name : '') || cityLike;
      const normalizeTopicName = (value) => String(value || '').trim().toLowerCase();
      const matchesSuggestedButtonTopic =
        normalizeTopicName(clickedTopicName || cityLike) &&
        normalizeTopicName(cityName) &&
        normalizeTopicName(clickedTopicName || cityLike) === normalizeTopicName(cityName);

      const countryName = getLong('country');
      const countryCode = getShort('country').toLowerCase();
      const displayName = (ok ? place.formatted_address : '') || clickedTopicName || `${lat}, ${lon}`;

      const syntheticLocation = {
        address: {
          city: clickedTopicName || cityLike,
          state: getLong('administrative_area_level_1') || '',
          country: countryName || '',
          country_code: countryCode || '',
          display_name: displayName,
        },
        name: clickedTopicName || cityLike,
        topic_place_name: clickedTopicName || cityLike,
        display_name: displayName,
        lat,
        lon,
        place_id: placeId,
      };

      const currentZoom = mapRef.current?.getZoom?.() ?? mapZoom ?? 10;
      const enrichedSyntheticLocation = attachTopicGeoContext(
        attachDerivedTopicSelection(syntheticLocation, currentZoom),
      );
      setDisplayLocation(enrichedSyntheticLocation);

      // Feed map click location to V2 store
      if (USE_DETAILS_CARD_V2) {
        useTopicStore.getState().handleNominatimResult(enrichedSyntheticLocation, {
          userLanguageLabel,
          viewportWikiLanguage,
        });
      }

      setIsMapCenteredOnUser(false);
      setIsFromSearchfield(false);
      setSearchFieldLocationString(null);
      setSearchFieldRequestRequired(false);
      setLastGeocodingCall(Date.now());

      const tryFetchWikipediaArticle = async (title, langCode) => {
        if (!title || !langCode) return null;
        const params = new URLSearchParams({
          action: 'query',
          titles: title,
          prop: 'pageimages|coordinates|info',
          inprop: 'url',
          pithumbsize: '400',
          format: 'json',
          origin: '*',
        });

        const url = `https://${langCode}.wikipedia.org/w/api.php?${params.toString()}`;
        const resp = await fetch(url);
        if (!resp.ok) return null;
        const data = await resp.json();
        const pages = data?.query?.pages || {};
        const pageId = Object.keys(pages)[0];
        const page = pageId ? pages[pageId] : null;
        if (!page || page.missing !== undefined || !page.title) return null;

        const coord = page.coordinates?.[0];
        const normalizedArticleSize = Number(page?.length ?? NaN);
        return {
          pageid: page.pageid,
          title: page.title,
          lat: coord?.lat ?? lat,
          lon: coord?.lon ?? lon,
          thumbnail: page.thumbnail?.source || FALLBACK_THUMBNAIL,
          fullurl: page.fullurl || null,
          pvSum: 0,
          sourceWikiLang: langCode,
          articleSizeBytes: Number.isFinite(normalizedArticleSize) && normalizedArticleSize > 0
            ? normalizedArticleSize
            : null,
        };
      };

      (async () => {
        try {
          // Use the clicked map name as Wikipedia topic first, fallback to English edition.
          let wikiArticle = await tryFetchWikipediaArticle(clickedTopicName || cityLike, viewportWikiLanguage);
          if (!wikiArticle && viewportWikiLanguage !== 'en') {
            wikiArticle = await tryFetchWikipediaArticle(clickedTopicName || cityLike, 'en');
          }

          if (wikiArticle) {
            if (matchesSuggestedButtonTopic) {
              log(`[Map] Clicked topic "${clickedTopicName || cityLike}" matches location button topic "${cityName}" – using location topic.`);
              if (detailsCardRef.current && typeof detailsCardRef.current.switchToLocation === 'function') {
                detailsCardRef.current.switchToLocation(syntheticLocation);
              }
              setSelectedArticle(null);
              useTopicStore.getState().activateSuggestedTopic({
                userLanguageLabel,
                viewportWikiLanguage,
                userPreferredWikiLangCode,
              });
            } else {
              setSelectedArticle(wikiArticle);
              useTopicStore.getState().setWikiArticleTopic(wikiArticle, {
                viewportWikiLanguage,
                userPreferredWikiLangCode,
              });
            }
          } else {
            // If no wiki page was found, keep location-topic fallback behavior.
            if (detailsCardRef.current && typeof detailsCardRef.current.switchToLocation === 'function') {
              detailsCardRef.current.switchToLocation(syntheticLocation);
            }
            setSelectedArticle(null);
          }
        } catch (error) {
          console.error('[Map] Failed to open clicked label as wiki topic:', error);
          if (detailsCardRef.current && typeof detailsCardRef.current.switchToLocation === 'function') {
            detailsCardRef.current.switchToLocation(syntheticLocation);
          }
          setSelectedArticle(null);
        }
      })();

      resolveDisplayedPlaceWikidata({
        placeName: clickedTopicName || cityLike,
        countryName,
        countryCode,
        lat,
        lon,
        topicAdminLevel: clickedTopicAdminLevel,
      }).then((qid) => {
        if (!qid) return;
        setDisplayLocation((prev) => {
          if (!prev) return prev;
          if (prev.topic_wikidata === qid) return prev;
          return {
            ...prev,
            topic_place_name: clickedTopicName || cityLike,
            topic_admin_level: prev.topic_admin_level || clickedTopicAdminLevel || '',
            topic_wikidata: qid,
            topic_population: null,
          };
        });

        prefetchTopicPopulation(qid)
          .then((population) => {
            if (!Number.isFinite(population) || population <= 0) return;
            setDisplayLocation((prev) => {
              if (!prev) return prev;
              if (prev.topic_wikidata !== qid) return prev;
              if (prev.topic_population === population) return prev;
              return {
                ...prev,
                topic_population: population,
              };
            });
          })
          .catch((error) => {
            console.error('[wikitag][Population] Failed to prefetch topic population after map click:', error);
          });
      }).catch((error) => {
        console.error('[wikitag][Map] Failed to resolve Wikidata from place click:', error);
      });
    });
  }, [
    mapRef,
    viewportWikiLanguage,
    userPreferredWikiLangCode,
    userLanguageLabel,
    userLanguage,
    cityName,
    mapZoom,
    markMapConfiguredByUser,
    prefetchTopicPopulation,
  ]);

  const activateStartupLocationTopic = useCallback((locationDataForTopic, wikiLanguageForTopic = viewportWikiLanguage) => {
    if (!locationDataForTopic || initialNominatimTopicSet || startupTopicActivationInFlightRef.current) {
      return false;
    }

    const pendingTarget = pendingStartupActivationRef.current ||
      (activeStartupLocation && activeStartupLocation !== 'gps' ? activeStartupLocation : null);

    if (pendingTarget) {
      const resultLat = Number(locationDataForTopic?.lat);
      const resultLon = Number(locationDataForTopic?.lon ?? locationDataForTopic?.lng);
      const matchesStartupTarget =
        Number.isFinite(resultLat) &&
        Number.isFinite(resultLon) &&
        Math.abs(resultLat - Number(pendingTarget.lat)) <= 0.75 &&
        Math.abs(resultLon - Number(pendingTarget.lng)) <= 0.75;

      if (!matchesStartupTarget) {
        log('[Startup] Waiting for reverse geocode result to catch up to selected landing location.', {
          target: pendingTarget,
          result: { lat: resultLat, lon: resultLon },
        });
        return false;
      }
    }

    log('[App] Nominatim data available for startup position, setting initial topic to current location.');
    startupTopicActivationInFlightRef.current = true;

    void (async () => {
      try {
        setSelectedArticle(null); // switch details card to location topic
        // Automatically open Top Sights carousel once after GPS/startup selection.
        setAutoShowLocationSights(true);

        if (USE_DETAILS_CARD_V2) {
          const topicStore = useTopicStore.getState();
          const topicResolution = await topicStore.handleNominatimResult(locationDataForTopic, {
            userLanguageLabel,
            viewportWikiLanguage: wikiLanguageForTopic,
          });
          const { topicBounds } = unpackTopicResolution(topicResolution);
          if (topicBounds) {
            setDisplayLocation((prev) => mergeTopicResolutionIntoLocation(
              prev,
              topicResolution,
              locationDataForTopic.topic_place_name,
              locationDataForTopic.topic_admin_level,
            ));
            log('[wikitag][Topic] Stored startup topic bounds from V2 Nominatim resolution', {
              placeName: locationDataForTopic.topic_place_name,
              topicAdminLevel: locationDataForTopic.topic_admin_level,
              topicBounds,
            });
          }
          useTopicStore.getState().activateSuggestedTopic({
            userLanguageLabel,
            viewportWikiLanguage: wikiLanguageForTopic,
            userPreferredWikiLangCode,
          });

          const { activeTopicId, suggestedTopicId } = useTopicStore.getState();
          if (!suggestedTopicId || activeTopicId !== suggestedTopicId) {
            console.warn('[Startup] Tried to activate startup location topic, but no matching suggested topic was ready.', {
              activeTopicId,
              suggestedTopicId,
            });
          }
        }

        setInitialNominatimTopicSet(true);
        pendingStartupActivationRef.current = null;
        cleanupStartupUrlParameters();
        if (typeof window !== 'undefined') {
          window.__CURIO_PENDING_SELECTED_LOCATION = null;
        }
      } catch (error) {
        console.error('[Startup] Failed to activate startup location topic:', error);
      } finally {
        startupTopicActivationInFlightRef.current = false;
      }
    })();

    return true;
  }, [
    activeStartupLocation,
    initialNominatimTopicSet,
    setAutoShowLocationSights,
    setInitialNominatimTopicSet,
    setSelectedArticle,
    userLanguageLabel,
    viewportWikiLanguage,
    userPreferredWikiLangCode,
  ]);

  // Effect to set initial topic to location view once Nominatim data is available
  // Only trigger for legitimate startup positions (GPS or URL parameters), not user panning
  useEffect(() => {
    if (initialLocationSet) {
      activateStartupLocationTopic(nominatimData, viewportWikiLanguage);
    }
  }, [
    activateStartupLocationTopic,
    nominatimData,
    initialLocationSet,
    viewportWikiLanguage,
  ]);

  // Create refs to store map-idle timing handles
  const onIdleTimeoutRef = useRef(null);
  const onIdleSettleTimeoutRef = useRef(null);

  const persistCurrentMapView = useCallback(() => {
    if (!mapRef.current) return;

    try {
      const center = mapRef.current.getCenter?.();
      const zoom = mapRef.current.getZoom?.();
      if (!center || !Number.isFinite(zoom)) return;

      const lat = center.lat();
      const lng = center.lng();
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return;

      localStorage.setItem(LAST_MAP_VIEW_KEY, JSON.stringify({
        lat,
        lng,
        zoom
      }));
    } catch (error) {
      console.warn('[Map] Failed to persist current map view:', error);
    }
  }, []);

  const panPointIntoVisibleMapArea = useCallback(({ lat, lng }) => {
    const map = mapRef.current;
    if (!map || !isValidLatLng(lat, lng)) return;

    const mapDiv = typeof map.getDiv === 'function' ? map.getDiv() : null;
    const mapHeightPx = Number(mapDiv?.clientHeight || 0);
    const occludedHeightPx = Math.max(0, Math.min(Number(detailsCardHeightPx) || 0, mapHeightPx));
    const verticalOffsetPx = occludedHeightPx > 0 ? Math.round(occludedHeightPx / 2) : 0;

    try {
      const projection = typeof map.getProjection === 'function' ? map.getProjection() : null;
      const zoom = Number(map.getZoom?.());
      const googleMaps = window.google?.maps;

      if (
        projection &&
        Number.isFinite(zoom) &&
        googleMaps?.LatLng &&
        googleMaps?.Point &&
        verticalOffsetPx > 0
      ) {
        const targetPoint = projection.fromLatLngToPoint(new googleMaps.LatLng(lat, lng));

        if (targetPoint) {
          const scale = 2 ** zoom;
          const headingDeg = Number(map.getHeading?.() || 0);
          const headingRad = (headingDeg * Math.PI) / 180;
          // Convert the screen-space "down" offset into world-point space so the
          // visible-area centering still works when the map is rotated.
          const offsetWorldX = (-Math.sin(headingRad) * verticalOffsetPx) / scale;
          const offsetWorldY = (Math.cos(headingRad) * verticalOffsetPx) / scale;
          const adjustedCenterPoint = new googleMaps.Point(
            targetPoint.x + offsetWorldX,
            targetPoint.y + offsetWorldY,
          );
          const adjustedCenterLatLng = projection.fromPointToLatLng(adjustedCenterPoint);

          if (adjustedCenterLatLng) {
            map.panTo(adjustedCenterLatLng);
          } else {
            map.panTo({ lat, lng });
          }
        } else {
          map.panTo({ lat, lng });
        }
      } else {
        map.panTo({ lat, lng });
      }

      setIsMapCenteredOnUser(false);
    } catch (error) {
      console.error('[Map] Error panning to visible map area:', error);
    }
  }, [detailsCardHeightPx, setIsMapCenteredOnUser]);

  const onIdle = useCallback(() => {
    // Clear any existing idle debounce/settle timeouts
    if (onIdleTimeoutRef.current) {
      clearTimeout(onIdleTimeoutRef.current);
    }
    if (onIdleSettleTimeoutRef.current) {
      clearTimeout(onIdleSettleTimeoutRef.current);
    }

    // Debounce the idle event, then wait a little longer for map projection/marker state to settle.
    onIdleTimeoutRef.current = setTimeout(() => {
      onIdleSettleTimeoutRef.current = setTimeout(() => {
      if (!mapRef.current) return;

      try {
        // Validate bounds before using them
        const bounds = mapRef.current.getBounds();
        if (!bounds) {
          console.warn('[Map] Invalid bounds detected, skipping update');
          return;
        }

        // Check if bounds are valid
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        if (!ne || !sw ||
          !isValidLatLng(ne.lat(), ne.lng()) ||
          !isValidLatLng(sw.lat(), sw.lng())) {
          console.warn('[Map] Invalid bounds coordinates detected, skipping update');
          return;
        }

        handleBoundsUpdate(bounds);

        // Debug geocoding comparison window disabled.
        // const centerForPhoton = mapRef.current.getCenter();
        // if (
        //   isLocalhost &&
        //   centerForPhoton &&
        //   isValidLatLng(centerForPhoton.lat(), centerForPhoton.lng())
        // ) {
        //   setPhotonCenter({ lat: centerForPhoton.lat(), lon: centerForPhoton.lng() });
        // }

        const zoom = mapRef.current.getZoom();

        // Skip heavy computations during rapid zoom changes to prevent lag
        const isZoomStable = !markerScaleTimeout; // Only do heavy work when zoom is stable

        if (isZoomStable) {
          // Calculate viewport width in Web Worker when zoom is stable
          mapComputeWorkerManager.calculateViewport(bounds, zoom)
            .then(result => {
              log(`Map zoom: ${result.zoom}, viewport width: ${result.widthKm.toFixed(2)} km`);
              // Apply marker scale from worker calculation
              const nextMarkerScale = Number(result.markerScale) || 1;
              document.documentElement.style.setProperty('--marker-scale', nextMarkerScale.toString());
              setMarkerVisualScale(nextMarkerScale);

            })
            .catch(error => {
              console.error('[Map] Viewport calculation error:', error);
              log(`Map zoom: ${zoom}, viewport calculation failed`);
            });
        } else {
          // During zoom gestures, just log zoom without heavy calculations
          log(`Map zoom: ${zoom} (calculating viewport...)`);
        }

        // Track zoom level changes for debouncing
        if (zoom !== lastZoomLevel) {
          setLastZoomLevel(zoom);

          // Clear existing timeout
          if (markerScaleTimeout) {
            clearTimeout(markerScaleTimeout);
          }

          // Set timeout to mark when zoom is stable (no longer changing)
          const newTimeout = setTimeout(() => {
            // This timeout firing means zoom has been stable for 800ms
            // The actual scaling is done by the worker in the isZoomStable block above
            setMarkerScaleTimeout(null); // Clear to indicate zoom is stable
          }, 800); // 800ms debounce for zoom stability

          setMarkerScaleTimeout(newTimeout);
        }

        // Only do geocoding and other heavy operations when zoom is stable
        if (isZoomStable) {
          // Throttle geocoding calls to once per second
          const now = Date.now();
          if (now - lastGeocodingCall >= GEOCODING_THROTTLE_MS) {
            const center = mapRef.current.getCenter();
            if (center && isValidLatLng(center.lat(), center.lng()) && geocoderRef.current) {
              setLastGeocodingCall(now); // Set immediately to prevent multiple calls

              const lat = center.lat();
              const lng = center.lng();
              const currentZoom = mapRef.current.getZoom();
              const cachedTopicHit = getCachedTopicBoundsHit(
                useAppStore.getState().displayLocation,
                lat,
                lng,
                currentZoom,
              );

              if (cachedTopicHit) {
                log('[Geocoding] Skipping Nominatim request; map center is within cached topic bounds', {
                  topicName: cachedTopicHit.topicName,
                  adminLevel: cachedTopicHit.adminLevel,
                  lat,
                  lng,
                  distanceFromTopicCenterKm: Math.round(cachedTopicHit.distanceFromTopicCenterKm * 100) / 100,
                  maxDistanceKm: MAX_CACHED_TOPIC_CENTER_DISTANCE_KM,
                  bounds: cachedTopicHit.bounds,
                });
              } else {
                // Use requestIdleCallback for lowest priority execution
                const geocodeTask = async () => {
                  try {
                    const data = await fetchGeocodingNominatim({
                      lat,
                      lng,
                      zoom: currentZoom,
                      userLanguage,
                    });
                    if (data) {
                      const enrichedLocationData = attachTopicGeoContext(
                        attachDerivedTopicSelection(data, currentZoom),
                      );
                      setDisplayLocation(enrichedLocationData);
                      const lang = getWikiLanguageFromNominatim(enrichedLocationData);
                      setViewportWikiLanguage(lang);
                      log(`Using wiki language: ${lang} based on geocoding data (map movement)`);

                      // Feed the already-fetched Nominatim data to the V2 store (no double call)
                      if (USE_DETAILS_CARD_V2) {
                        const startupActivationStarted = activateStartupLocationTopic(enrichedLocationData, lang);
                        if (!startupActivationStarted) {
                          const topicResolutionPromise = useTopicStore.getState().handleNominatimResult(enrichedLocationData, {
                            userLanguageLabel,
                            viewportWikiLanguage: lang,
                          });
                          topicResolutionPromise?.then((topicResolution) => {
                            const { topicBounds } = unpackTopicResolution(topicResolution);
                            if (!topicBounds) return;
                            setDisplayLocation((prev) => mergeTopicResolutionIntoLocation(
                              prev,
                              topicResolution,
                              enrichedLocationData.topic_place_name,
                              enrichedLocationData.topic_admin_level,
                            ));
                            log('[wikitag][Topic] Stored topic bounds from V2 Nominatim resolution', {
                              placeName: enrichedLocationData.topic_place_name,
                              topicAdminLevel: enrichedLocationData.topic_admin_level,
                              topicBounds,
                            });
                          }).catch((error) => {
                            console.error('[wikitag][Topic] Failed to store topic bounds from V2 resolution:', error);
                          });
                        }
                      }
                    }
                  } catch (error) {
                    console.error('[Map] Geocoding fetch error:', error);
                  }
                };

                // Use requestIdleCallback if available, otherwise setTimeout
                if ('requestIdleCallback' in window) {
                  requestIdleCallback(geocodeTask, { timeout: 2000 });
                } else {
                  setTimeout(geocodeTask, 100);
                }
              }
            } else {
              console.warn('[Map] Invalid center coordinates or geocoder not ready, skipping geocoding fetch');
            }
          }
        }

        // Store zoom level in state for conditional rendering
        setMapZoom(zoom);
        // Also update signal for cityName computation
        setMapZoomSignal(zoom);

        // Persist map position/zoom so startup can restore this view.
        persistCurrentMapView();
      } catch (error) {
        console.error('[Map] onIdle error:', error);
      }
      }, 200);
    }, 150);
  }, [activateStartupLocationTopic, handleBoundsUpdate, lastGeocodingCall, userLanguage, markerScaleTimeout, lastZoomLevel, persistCurrentMapView, userLanguageLabel]);

  // Periodically persist map position/zoom while the app is open.
  useEffect(() => {
    const intervalId = setInterval(() => {
      persistCurrentMapView();
    }, LAST_MAP_VIEW_SAVE_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [persistCurrentMapView]);

  // NEW: Log tilt whenever the user changes the map pitch
  const handleTiltChanged = useCallback(() => {
    if (mapRef.current) {
      //const tilt = mapRef.current.getTilt();
      ///  log(`Map tilt changed: ${tilt}°`);
    }
  }, []);

  // Cleanup marker scaling timeout on unmount
  useEffect(() => {
    return () => {
      if (markerScaleTimeout) {
        clearTimeout(markerScaleTimeout);
      }
    };
  }, [markerScaleTimeout]);

  // Cleanup map worker and pending timeouts on unmount
  useEffect(() => {
    return () => {
      mapComputeWorkerManager.terminate();
      // Clear onIdle timeouts if pending
      if (onIdleTimeoutRef.current) {
        clearTimeout(onIdleTimeoutRef.current);
      }
      if (onIdleSettleTimeoutRef.current) {
        clearTimeout(onIdleSettleTimeoutRef.current);
      }
      if (mapGestureCleanupRef.current) {
        mapGestureCleanupRef.current();
        mapGestureCleanupRef.current = null;
      }
    };
  }, []);

  // Effect to handle initial map centering if userPosition arrives after map load
  useEffect(() => {
    console.log(`use effect that handles initial map centering triggered by change of isLoaded (now: ${isLoaded}), position (now: ${gps.position ? 'present' : 'null'}), initialLocationSet (now: ${initialLocationSet})`);
    if (isLoaded && mapRef.current && gps.position && !initialLocationSet && (!activeStartupLocation || activeStartupLocation === 'gps')) {
      centerMapOnUser(); // Use the centralized function
      setInitialLocationSet(true);
    }
  }, [isLoaded, gps.position, initialLocationSet, centerMapOnUser, activeStartupLocation]);

  // Effect to handle GPS unavailable states and loading states
  useEffect(() => {
    if (!initialNominatimTopicSet && (gps.isError || gps.isLoading)) {
      setSelectedArticle(MOVE_MAP_PROMPT_ARTICLE);
      useTopicStore.getState().setWikiArticleTopic(MOVE_MAP_PROMPT_ARTICLE, {
        viewportWikiLanguage,
        userPreferredWikiLangCode,
      });
    }
  }, [gps.isError, gps.isLoading, initialNominatimTopicSet, viewportWikiLanguage, userPreferredWikiLangCode]);

  // Effect to handle startup location from URL parameters (higher priority than GPS)
  useEffect(() => {
    if (isLoaded && mapRef.current && activeStartupLocation && !initialLocationSet) {
      console.log('[Startup] Applying selected startup location:', activeStartupLocation);
      try {
        if (activeStartupLocation === 'gps') {
          // Special case: startup location requests GPS, but only if GPS is available
          if (gps.position && isValidCoordinate(gps.position)) {
            pendingStartupActivationRef.current = {
              lat: Number(gps.position.lat),
              lng: Number(gps.position.lng),
            };
            centerMapOnUser();
            setInitialLocationSet(true);
            console.log('[Startup] Applied GPS location from startup parameter');

            // Clean up URL parameters after successful GPS initialization
            cleanupStartupUrlParameters();
          }
          // If GPS not available yet, wait for it or fallback will handle it
        } else if (isValidCoordinate(activeStartupLocation)) {
          pendingStartupActivationRef.current = {
            lat: Number(activeStartupLocation.lat),
            lng: Number(activeStartupLocation.lng),
          };
          mapRef.current.setCenter(activeStartupLocation);
          mapRef.current.setZoom(activeStartupLocation.zoom || 12);
          setInitialLocationSet(true);
          setInitialNominatimTopicSet(false);
          setIsMapCenteredOnUser(false); // Not user location
          setLastGeocodingCall(0);
          console.log('[Startup] Applied predefined location:', activeStartupLocation);

          // Clean up URL parameters after successful predefined location initialization
          cleanupStartupUrlParameters();
          if (typeof window !== 'undefined') {
            window.__CURIO_PENDING_SELECTED_LOCATION = null;
          }
        }
      } catch (error) {
        console.error('[Startup] Error applying startup location:', error);
      }
    }
  }, [
    isLoaded,
    activeStartupLocation,
    initialLocationSet,
    gps.position,
    centerMapOnUser,
    setInitialNominatimTopicSet,
    setLastGeocodingCall,
    setIsMapCenteredOnUser,
  ]);

  // Effect to refetch geocoding data when the user changes the interface language
  useEffect(() => {
    console.log(`[App] userLanguage changed to ${userLanguage}, fetching updated geocoding data.`);
    if (!mapRef.current || !geocoderRef.current) return; // Map or geocoder not ready yet

    const center = mapRef.current.getCenter();
    if (!center) return; // No center available

    (async () => {
      const lat = center.lat();
      const lng = center.lng();
      const currentZoom = mapRef.current ? mapRef.current.getZoom() : 10;
      const data = await fetchGeocodingNominatim({
        lat,
        lng,
        zoom: currentZoom,
        userLanguage,
      });
      if (data) {
        setDisplayLocation(attachTopicGeoContext(attachDerivedTopicSelection(data, currentZoom)));
        const lang = getWikiLanguageFromNominatim(data);
        setViewportWikiLanguage(lang);
        log(`Using wiki language: ${lang} based on geocoding data (language change)`);
        // Don't reset search field tracking here - only reset after the search field request is made
      }
    })();
    // Update throttle timestamp so the onIdle throttling stays in sync
    setLastGeocodingCall(Date.now());
  }, [userLanguage]);



  // cityName and nominatimData now come from signals (defined above)

  /* --------------------------------------------------------------
   * Top Sights (golden-star markers) – hooks must come BEFORE any
   * early return so ESLint's rules-of-hooks are satisfied.
   * ------------------------------------------------------------*/
  const handleSightsReady = useCallback((arr) => {
    setTopSightsList(Array.isArray(arr) ? arr : []);
  }, []);

  const goldStarIcon = useMemo(() => {
    if (!isLoaded || !window.google) return null;
    const svg = `\
      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='#FFD700' stroke='#FFFFFF' stroke-width='1'>\
        <polygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2'/>\
      </svg>`;
    return {
      url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(28, 28),
      anchor: new window.google.maps.Point(14, 14),
    };
  }, [isLoaded]);

  // Lime star variant used when a Top Sight lacks thumbnail and is selected
  const limeStarIcon = useMemo(() => {
    if (!isLoaded || !window.google) return null;
    const svg = `\
      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='#32CD32' stroke='#FFFFFF' stroke-width='1'>\
        <polygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2'/>\
      </svg>`;
    return {
      url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(28, 28),
      anchor: new window.google.maps.Point(14, 14),
    };
  }, [isLoaded]);

  // Central helper – select an article and manage history
  const selectArticle = useCallback((input, fromHistory = false) => {
    // Track user activity
    if (!fromHistory) {
      activityTracker.updateLastActivity();
    }

    // Keep top-sight marker highlight in sync with explicit selection.
    if (input?.isTopSight) {
      const markerId = input?.pageid || input?.id || input?.link || null;
      setFocusedTopSightMarkerId(markerId ? String(markerId) : null);
    } else if (!input || input?.type === 'location') {
      setFocusedTopSightMarkerId(null);
    }

    // Determine if this is an article or a request to switch to location view (null)
    const topicGeoContext = extractTopicGeoContext(nominatimData);
    let entry;
    if (!input || (input && input.type === 'location')) {
      // Treat as location entry
      const center = mapRef.current ? mapRef.current.getCenter() : null;
      entry = {
        type: 'location',
        key: `loc-${Date.now()}`,
        city: cityName,
        lat: center ? center.lat() : null,
        lon: center ? center.lng() : null,
        topicCountry: topicGeoContext.country || '',
        topicSecondMostZoomedOutFeature: topicGeoContext.secondMostZoomedOutFeature || '',
      };
      setSelectedArticle(null);

      // Don't reset search field tracking here - only reset after the search field request is made

      // Log location visit (only if not navigating from history)
      if (!fromHistory && nominatimData) {
        const mapCenter = center ? { lat: center.lat(), lng: center.lng() } : null;
        logLocationVisit(nominatimData, mapCenter);
      }

      // Resolve a Wikidata QID for the place that is currently used as topic
      // via a second Nominatim reverse call that is narrowed to the chosen
      // topic admin level and includes extratags.
      const displayedPlaceName = cityName || nominatimData?.address?.city || nominatimData?.address?.town || nominatimData?.address?.village || nominatimData?.address?.county || nominatimData?.address?.state || nominatimData?.address?.country;
      const existingTopicWikidata = nominatimData?.topic_wikidata;
      const topicAdminLevel = nominatimData?.topic_admin_level || locationTopicMeta?.adminLevel || '';

      if (displayedPlaceName && center) {
        const centerLat = center.lat();
        const centerLon = center.lng();

        log('[wikitag][Topic] Starting topic Wikidata resolution from Nominatim', {
          placeName: displayedPlaceName,
          topicAdminLevel,
          lat: centerLat,
          lon: centerLon,
          strategy: 'second-reverse-call-with-extratags',
          existingTopicWikidata: existingTopicWikidata || null,
        });

        fetchTopicWikidataFromNominatim({
          lat: centerLat,
          lng: centerLon,
          placeName: displayedPlaceName,
          topicAdminLevel,
          userLanguage,
        }).then((topicResolution) => {
          const qid = typeof topicResolution === 'string'
            ? topicResolution
            : topicResolution?.qid || null;
          const topicBounds = typeof topicResolution === 'object'
            ? topicResolution?.topicBounds || null
            : null;

          if (!qid && !topicBounds) {
            log('[wikitag][Topic] Nominatim topic resolution returned no QID or bounds', {
              placeName: displayedPlaceName,
              topicAdminLevel,
            });
            return;
          }

          setDisplayLocation((prev) => {
            if (!prev) return prev;
            if (prev.topic_wikidata === qid && prev.topic_bounds === topicBounds) return prev;
            return {
              ...prev,
              topic_place_name: displayedPlaceName,
              topic_admin_level: prev.topic_admin_level || topicAdminLevel,
              ...(qid ? { topic_wikidata: qid, topic_population: null } : {}),
              ...(topicBounds ? { topic_bounds: topicBounds } : {}),
            };
          });

          if (!qid) {
            log('[wikitag][Topic] Stored topic bounds without Wikidata QID', {
              placeName: displayedPlaceName,
              topicAdminLevel,
              topicBounds,
            });
            return;
          }

          prefetchTopicPopulation(qid)
            .then((population) => {
              if (!Number.isFinite(population) || population <= 0) return;
              setDisplayLocation((prev) => {
                if (!prev) return prev;
                if (prev.topic_wikidata !== qid) return prev;
                if (prev.topic_population === population) return prev;
                return {
                  ...prev,
                  topic_population: population,
                };
              });
            })
            .catch((error) => {
              console.error('[wikitag][Population] Failed to prefetch topic population after Nominatim QID resolution:', error);
            });

          log('[wikitag][Topic] Resolved topic metadata from Nominatim', {
            placeName: displayedPlaceName,
            topicAdminLevel,
            qid,
            hasTopicBounds: !!topicBounds,
          });
        }).catch((error) => {
          console.error('[wikitag][Topic] Nominatim Wikidata resolution failed:', error);
        });
      } else {
        log('[wikitag][Topic] Skipping topic Wikidata resolution', {
          reason: !displayedPlaceName ? 'missing-topic-place-name' : 'missing-map-center',
          existingTopicWikidata,
          displayedPlaceName,
          hasCenter: !!center,
          topicAdminLevel,
        });

        if (existingTopicWikidata && !nominatimData?.topic_population) {
          prefetchTopicPopulation(existingTopicWikidata)
            .then((population) => {
              if (!Number.isFinite(population) || population <= 0) return;
              setDisplayLocation((prev) => {
                if (!prev) return prev;
                if (prev.topic_wikidata !== existingTopicWikidata) return prev;
                if (prev.topic_population === population) return prev;
                return {
                  ...prev,
                  topic_population: population,
                };
              });
            })
            .catch((error) => {
              console.error('[wikitag][Population] Failed to prefetch topic population for existing topic_wikidata:', error);
            });
        }
      }
    } else {
      // It's an article (either from Wikipedia search or a Top Sight)
      const articleInput = {
        ...input,
        sourceWikiLang: input.sourceWikiLang || viewportWikiLanguage,
      };

      const normalizeTopicName = (value) => String(value || '').trim().toLowerCase();
      const articleMatchesSuggestedLocationTopic =
        !fromHistory &&
        normalizeTopicName(articleInput.title) &&
        normalizeTopicName(cityName) &&
        normalizeTopicName(articleInput.title) === normalizeTopicName(cityName);

      if (articleMatchesSuggestedLocationTopic) {
        log(`[Topic] Clicked topic "${articleInput.title}" matches location button topic "${cityName}" – treating as location topic.`);
        const center = mapRef.current ? mapRef.current.getCenter() : null;
        entry = {
          type: 'location',
          key: `loc-${Date.now()}`,
          city: cityName,
          lat: center ? center.lat() : null,
          lon: center ? center.lng() : null,
          topicCountry: topicGeoContext.country || '',
          topicSecondMostZoomedOutFeature: topicGeoContext.secondMostZoomedOutFeature || '',
        };
        setSelectedArticle(null);
        useTopicStore.getState().activateSuggestedTopic({
          userLanguageLabel,
          viewportWikiLanguage,
          userPreferredWikiLangCode,
        });
      } else {
        entry = {
          type: 'article',
          ...articleInput, // Spread all properties from input (pageid, title, lat, lon, thumbnail, fullurl)
          topicCountry: topicGeoContext.country || '',
          topicSecondMostZoomedOutFeature: topicGeoContext.secondMostZoomedOutFeature || '',
          // If it's a top sight, input will have originalDescription and isTopSight
        };
        setSelectedArticle(articleInput); // Pass the enriched input directly
        useTopicStore.getState().setWikiArticleTopic(articleInput, {
          viewportWikiLanguage,
          userPreferredWikiLangCode,
        });

      // Reset search field tracking when selecting an article (user explicitly chose something else)
      if (!fromHistory) {
        setIsFromSearchfield(false);
        setSearchFieldLocationString(null);
        setSearchFieldRequestRequired(false);
      }

      // Log Wikipedia pageviews for the selected article (if available)
      log(`Opening article "${articleInput.title}" (pageid: ${articleInput.pageid}) – pageviews: ${articleInput.pvSum ?? 'unknown'}`);

      // Keep the selected sight/article centered in the visible map area above the card.
      if (articleInput.lat != null && articleInput.lon != null) {
        if (isValidLatLng(articleInput.lat, articleInput.lon)) {
          panPointIntoVisibleMapArea({ lat: articleInput.lat, lng: articleInput.lon });
        } else {
          console.warn('[Map] Invalid article coordinates, skipping pan:', articleInput.lat, articleInput.lon);
        }
      }

      // Log article visit (only if not navigating from history)
      if (!fromHistory) {
        const center = mapRef.current ? mapRef.current.getCenter() : null;
        const mapCenter = center ? { lat: center.lat(), lng: center.lng() } : null;
        logArticleVisit(articleInput, nominatimData, mapCenter);
      }
      }
    }

    // If navigating through history, don't push new entry
    if (fromHistory) return;

    setHistory(prevHistory => {
      let working = prevHistory;
      if (historyIndexRef.current < prevHistory.length - 1) {
        working = prevHistory.slice(0, historyIndexRef.current + 1);
      }

      // Deduplicate: if same type and identifier as last, skip push
      const last = working[working.length - 1];
      const isDuplicate = last && last.type === entry.type &&
        ((entry.type === 'article' && last.pageid === entry.pageid && last.title === entry.title) || // Added title to dedupe check for articles
          (entry.type === 'location' && last.city === entry.city));
      if (isDuplicate) return working;

      const updated = [...working, entry];
      historyIndexRef.current = updated.length - 1;
      setHistoryIndex(historyIndexRef.current);
      return updated;
    });
  }, [
    cityName,
    locationTopicMeta,
    mapRef,
    nominatimData,
    panPointIntoVisibleMapArea,
    viewportWikiLanguage,
    userPreferredWikiLangCode,
    userLanguageLabel,
    userLanguage,
    prefetchTopicPopulation,
  ]);

  const showTopSightOnMap = useCallback((sight) => {
    const lat = Number(sight?.lat ?? sight?.coords?.lat);
    const lon = Number(sight?.lon ?? sight?.coords?.lon);

    if (!Number.isFinite(lat) || !Number.isFinite(lon) || !mapRef.current) {
      return;
    }

    if (!isValidLatLng(lat, lon)) {
      console.warn('[Map] Invalid top sight coordinates, skipping pan:', lat, lon);
      return;
    }

    try {
      // "Show on map" should not behave like "Set as topic".
      // Clear selected article so top-sight marker does not switch to the green selected state.
      setSelectedArticle(null);
      panPointIntoVisibleMapArea({ lat, lng: lon });
    } catch (error) {
      console.error('[Map] Error panning to top sight location:', error);
    }
  }, [mapRef, panPointIntoVisibleMapArea, setSelectedArticle]);

  const handleTopSightFocus = useCallback((sight) => {
    const markerId = sight?.id || sight?.link || null;
    setFocusedTopSightMarkerId(markerId ? String(markerId) : null);
  }, []);

  const goBack = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      setHistoryIndex(historyIndexRef.current);
      const prev = history[historyIndexRef.current];
      selectArticle(prev, true);
    }
  }, [history, selectArticle]);

  const goForward = useCallback(() => {
    if (historyIndexRef.current < history.length - 1) {
      historyIndexRef.current += 1;
      setHistoryIndex(historyIndexRef.current);
      const next = history[historyIndexRef.current];
      selectArticle(next, true);
    }
  }, [history, selectArticle]);

  useDevCliBridge({
    mapRef,
    selectedArticle,
    cityName,
    historyIndex,
    historyLength: history.length,
    setIsMapCenteredOnUser,
    selectArticle,
    detailsCardRef,
    narration,
    nominatimData,
    goBack,
    goForward,
    onMapConfigured: markMapConfiguredByUser,
  });

  useEffect(() => {
    const isGPSUnavailable = [GPS_STATUS.DENIED, GPS_STATUS.UNAVAILABLE, GPS_STATUS.TIMEOUT, GPS_STATUS.UNSUPPORTED].includes(gps.gpsStatus);

    if (
      isGPSUnavailable &&
      isLoaded &&
      mapRef.current &&
      !initialMapConfigured &&
      !activeStartupLocation &&
      !userHasMovedMapRef.current
    ) {
      console.log('[App] GPS unavailable – setting default map view to Europe.');
      if (isValidCoordinate(NO_GPS_CENTER)) {
        try {
          mapRef.current.setCenter(NO_GPS_CENTER);
          mapRef.current.setZoom(4);
          setInitialMapConfigured(true);
        } catch (error) {
          console.error('[Map] Error setting default center:', error);
        }
      }
    } else if (gps.hasPosition && !initialMapConfigured) {
      // GPS available – configuration will be handled by centering on user elsewhere
      setInitialMapConfigured(true);
    }
  }, [activeStartupLocation, gps.gpsStatus, gps.hasPosition, isLoaded, initialMapConfigured, setInitialMapConfigured]);

  // When the user selects a place from the search box we want to (1) pan the
  // map (SearchField already did that), (2) IMMEDIATELY fetch fresh Nominatim
  // data so DetailsCard can switch to the new location topic without waiting
  // for the next onIdle cycle, and (3) push a new "location" entry into the
  // history stack via selectArticle(null).
  const handleLocationSelected = useCallback((place, location) => {
    // Track user activity
    activityTracker.updateLastActivity();

    log(`Location selected: ${place.name || place.formatted_address}`, location);

    // The map has already been moved by SearchField.
    markMapConfiguredByUser();

    // Track that this location comes from search field
    setIsFromSearchfield(true);
    setSearchFieldLocationString(place.formatted_address || place.name || '');
    setSearchFieldRequestRequired(true);

    // Build a minimal location object from the Google Place data so we don't
    // have to wait for Nominatim. This keeps the shape compatible with
    // existing helpers that expect `address` and `lat`/`lon`.
    const components = Array.isArray(place?.address_components) ? place.address_components : [];
    const getLong = (type) => components.find((c) => c.types?.includes(type))?.long_name || '';
    const getShort = (type) => components.find((c) => c.types?.includes(type))?.short_name || '';

    const syntheticLocation = {
      address: {
        city: place.name || getLong('locality') || '',
        county: getLong('administrative_area_level_2') || '',
        state: getLong('administrative_area_level_1') || '',
        country: getLong('country') || '',
        country_code: String(getShort('country') || '').toLowerCase(),
        display_name: place.formatted_address || place.name || '',
      },
      display_name: place.formatted_address || place.name || '',
      lat: location.lat,
      lon: location.lng,
    };

    const currentZoom = mapRef.current?.getZoom?.() ?? mapZoom ?? 10;
    setDisplayLocation(attachTopicGeoContext(attachDerivedTopicSelection(syntheticLocation, currentZoom)));

    // No change to viewportWikiLanguage here – keep whatever was in use.

    // Notify DetailsCard to actively switch to this location topic.
    if (detailsCardRef.current && typeof detailsCardRef.current.switchToLocation === 'function') {
      detailsCardRef.current.switchToLocation(syntheticLocation);
    }

    // Keep the idle throttle timestamp in sync to avoid an immediate real
    // geocoding call; if the user pans afterwards, the normal idle handler
    // will eventually refresh.
    setLastGeocodingCall(Date.now());

    // Switch DetailsCard into LOCATION mode immediately and push a history
    // entry.  This also clears any previously selected article.
    selectArticle(null);
  }, [markMapConfiguredByUser, userLanguage, selectArticle, mapZoom]);

  // Handler for playing audio from history popup
  const handleHistoryAudioSelect = useCallback((event) => {
    if (!event || !event.audioCall) return;

    // Determine if this audio call is related to an article
    let artObj = null;
    if (event.article && event.article.pageid) {
      artObj = {
        pageid: event.article.pageid,
        title: event.article.title,
        lat: event.article.lat,
        lon: event.article.lng,
        thumbnail: FALLBACK_THUMBNAIL,
        fullurl: null,
        pvSum: 0,
      };
      selectArticle(artObj);
    } else {
      // Switch to location topic (null article)
      selectArticle(null);
    }

    // Start narration again with stored mode/topic
    const mode = event.audioCall.mode || 'overview';
    const topic = event.audioCall.topic;
    const historyReplayParams = {
      ...(event.audioCall?.cacheKey ? { forceCacheKey: event.audioCall.cacheKey } : {}),
      topicId: artObj
        ? `article:${artObj.sourceWikiLang || ''}:${artObj.pageid || artObj.title || ''}`
        : `location:${nominatimData?.topic_wikidata || topic}`,
    };
    narration.startNarration(topic, mode, historyReplayParams, nominatimData, artObj);

    // Close history popup
    setShowUserHistory(false);
  }, [selectArticle, narration, nominatimData]);

  const thumbnailBase64Cache = useRef({});

  const debouncedSetNotCentered = useCallback(debounce(() => setIsMapCenteredOnUser(false), 500), [setIsMapCenteredOnUser]);

  // Map ID required for Advanced Markers (replace with your own in production)
  const MAP_ID = '9db08db6071916179f5e6546';

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    gestureHandling: 'greedy',
    mapId: MAP_ID,
    mapTypeId,
    minZoom: 4,
    maxZoom: 20,
    isFractionalZoomEnabled: false,
    clickableIcons: true,
    rotateControl: false,
    scaleControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    restriction: {
      // Add world bounds restriction to prevent infinite panning
      latLngBounds: {
        north: 85,
        south: -85,
        west: -180,
        east: 180
      },
      strictBounds: false
    }
  }), [mapTypeId]);

  if (loadError) {
    console.error('[Map] Google Maps loading error:', loadError);
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#d32f2f', marginBottom: '16px' }}>Map Loading Error</h2>
        <p style={{ color: '#666', marginBottom: '16px' }}>Unable to load Google Maps. This could be due to:</p>
        <ul style={{ textAlign: 'left', color: '#666', marginBottom: '20px' }}>
          <li>Network connectivity issues</li>
          <li>Google Maps API quota exceeded</li>
          <li>Invalid API key configuration</li>
          <li>Browser compatibility issues</li>
        </ul>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Reload Page
        </button>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        fontSize: '18px',
        color: '#666'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ThinkingIndicator />
        </div>
      </div>
    );
  }

  const goToMyLocation = () => {
    // Track user activity
    activityTracker.updateLastActivity();

    if (gps.position && mapRef.current && isValidCoordinate(gps.position)) {
      try {
        centerMapOnUser(); // Use the centralized function
      } catch (error) {
        console.error('[Map] Error going to user location:', error);
        alert('Unable to center on your location. Please try again.');
      }
    } else {
      console.log("User position not available or invalid to center map.");
    }
  };

  // NEW: Toggle satellite / roadmap view
  const toggleMapType = () => {
    // Track user activity
    activityTracker.updateLastActivity();

    if (mapRef.current) {
      const currentType = mapRef.current.getMapTypeId();
      const nextType = currentType === 'satellite' ? 'roadmap' : 'satellite';
      // Apply immediately for visual feedback
      mapRef.current.setMapTypeId(nextType);
      // Store in state so React-provided options remain in sync and survive re-renders
      setMapTypeId(nextType);
    }
  };

  // PWA Installation handler
  const handlePwaInstallClick = async () => {
    console.log('[PWA] Install button clicked');

    if (pwaInstall.isInstalled) {
      console.log('[PWA] App is already installed and running in standalone mode');
      return;
    }

    // Android: Check if app is installed but running in browser
    if (pwaInstall.shouldShowOpenInApp) {
      console.log('[PWA] Android app installed but running in browser, attempting to open in app');
      const success = pwaInstall.openInstalledApp();
      if (!success) {
        console.log('[PWA] Failed to open in app, fallback to manual instructions');
        setShowAndroidInstallManual(true);
      }
      return;
    }

    // iOS: Show manual installation instructions
    if (pwaInstall.isIOS) {
      console.log('[PWA] iOS detected, showing manual installation instructions');
      setShowIOSInstallManual(true);
      return;
    }

    // Android: Try automatic installation first, fallback to manual
    if (pwaInstall.isAndroid) {
      if (pwaInstall.isInstallable) {
        console.log('[PWA] Android with install prompt available, attempting automatic installation');
        const success = await pwaInstall.installPWA();
        if (!success) {
          console.log('[PWA] Automatic installation failed, showing manual instructions');
          setShowAndroidInstallManual(true);
        }
      } else {
        console.log('[PWA] Android without install prompt, showing manual installation instructions');
        setShowAndroidInstallManual(true);
      }
      return;
    }

    // Desktop: Use PWA prompt directly, no manual instructions
    if (pwaInstall.isDesktop) {
      if (pwaInstall.isInstallable) {
        console.log('[PWA] Desktop with install prompt available, attempting automatic installation');
        const success = await pwaInstall.installPWA();
        if (!success) {
          console.log('[PWA] Desktop installation failed or was cancelled by user');
        }
      } else {
        console.log('[PWA] Desktop without install prompt available – installation not supported in this browser');
        window.alert('Installation is not supported in this browser. Try Chrome, Edge, or another PWA-capable browser.');
      }
      return;
    }

    // Fallback for other/unknown platforms – installation not supported
    console.log('[PWA] Other/unknown platform – PWA installation not supported');
    window.alert('Installation is not supported on this platform / browser.');
  };

  // SVG for location icon - different states based on map centering
  const standardLocationIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <g fill="none" stroke="white" stroke-width="2">
        <circle cx="12" cy="12" r="5" stroke="white"/>
        <circle cx="12" cy="12" r="9" opacity="0.4"/>
      </g>
    </svg>
  `;

  const centeredLocationIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <g>
        <circle cx="12" cy="12" r="5" fill="#4285F4" stroke="white" stroke-width="2"/>
        <circle cx="12" cy="12" r="9" stroke="#4285F4" stroke-width="2" opacity="0.3" fill="none"/>
      </g>
    </svg>
  `;

  const locationIcon = isMapCenteredOnUser ? centeredLocationIcon : standardLocationIcon;

  // Function to show tooltip when disabled buttons are clicked
  const showDisabledTooltip = () => {
    if (showTooltip) return; // Prevent multiple tooltips
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2000); // Hide after 2 seconds
  };

  // Desktop location button follows DetailsCard height (similar behavior to zoom controls)
  const desktopLocationButtonBottom = Math.max(170, Math.round(detailsCardHeightPx + 64));

  return (
    <div
      style={{
        position: 'relative',
        '--details-card-height': `${detailsCardHeightPx || 420}px`,
      } as React.CSSProperties}
    >
      {showWelcomeScreen && <WelcomeScreen onClose={handleWelcomeScreenClose} />}
      {/* UI Controls Container */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        right: '20px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        pointerEvents: 'none'
      }}>
        {/* Search Field - Full width at top */}
        <SearchField
          map={mapRef.current}
          isLoaded={isLoaded}
          onLocationSelected={handleLocationSelected}
          onMapMove={setIsMapCenteredOnUser}
          placeholder="Search for a location..."
          onLanguageMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
          isLanguageMenuOpen={isMenuOpen}
          showGuestIndicator={isGuestAccount}
        />


      </div>

      {/* GPS location button is now rendered inside DetailsCard, next to the Get Pro button */}

      {/* Main Menu Component */}
      <MainMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        userLanguage={userLanguage}
        onLanguageChange={handleLanguageChange}
        onSignInClick={() => loginMachine.requestLogin('manual', { preferQuickPrompt: false })}
        showGuestPrompt={isGuestAccount}
        showPwaInstallButton={showPwaInstallButton}
        onPwaInstallClick={handlePwaInstallClick}
      />


      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={activeStartupLocation && activeStartupLocation !== 'gps' ? activeStartupLocation : DEFAULT_CENTER}
        zoom={activeStartupLocation && activeStartupLocation !== 'gps' ? (activeStartupLocation.zoom || 12) : 5}
        onLoad={onLoad}
        onIdle={onIdle}
        onClick={handleMapClick}
        onDragStart={markMapConfiguredByUser}
        onZoomChanged={debouncedSetNotCentered}
        onTiltChanged={handleTiltChanged}
        mapId={MAP_ID}
        options={mapOptions}
      >
        {gps.position && (
          <AdvancedSvgMarker
            map={mapRef.current}
            position={gps.position}
            svg={`
              <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="10" fill="blue" stroke="white" stroke-width="2"/>
                <path d="M16 16 L20 12 L16 8" fill="none" stroke="blue" stroke-width="2" transform="rotate(${gps.orientation} 16 16)"/>
              </svg>`}
            size={32}
          />
        )}
        {/* Article markers */}
        {(() => {
          // Unified marker sizing – use a single base size for all markers.
          const baseSize = ORIGINAL_TOTAL_SIZE; // ≈50px before zoom scaling
          // Smaller size for simple dot markers (no thumbnail)
          const smallScale = 0.27;                // 27% of the normal marker size (25% smaller than before)
          const fallbackSize = baseSize * smallScale;

          // Geometry helpers for both sizes
          const dotCenter = baseSize / 2;
          const dotRadius = baseSize / 3.2; // keeps similar visual ratio as before

          const fallbackDotCenter = fallbackSize / 2;
          const fallbackDotRadius = fallbackSize / 3.2;

          return articles
            .sort((a, b) => a.pvSum - b.pvSum)
            .map((art) => {
              const isSelectedArticleMarker = selectedArticle?.pageid === art.pageid;
              const isHiddenByTopSight = hiddenArticleMarkerIds.has(art.pageid) && !isSelectedArticleMarker;
              const isDotOnlyArticleMarker = art.thumbnail === FALLBACK_THUMBNAIL ||
                (!articleIcons[art.pageid] && displayedArticles.has(art.pageid));
              const isHiddenBlueDotAtHighZoom = isDotOnlyArticleMarker && mapZoom > BLUE_DOT_MARKER_MAX_ZOOM;

              if (isHiddenByTopSight || isHiddenBlueDotAtHighZoom) return null;

              return (
                <React.Fragment key={art.pageid}>
                  {/* Main article marker */}
                  {art.thumbnail === FALLBACK_THUMBNAIL ? (
                    <AdvancedSvgMarker
                      map={mapRef.current}
                      position={{ lat: art.lat, lng: art.lon }}
                      svg={`
                        <svg width="${fallbackSize}" height="${fallbackSize}" viewBox="0 0 ${fallbackSize} ${fallbackSize}" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="${fallbackDotCenter}" cy="${fallbackDotCenter}" r="${fallbackDotRadius}" fill="${isSelectedArticleMarker ? '#32CD32' : DOT_FILL}" stroke="white" stroke-width="2"/>
                        </svg>`}
                      size={fallbackSize}
                      zIndex={art.pvSum}
                      onClick={() => selectArticle(art)}
                      extraClass={isSelectedArticleMarker ? 'selected-marker' : ''}
                      {...(mapZoom >= ARTICLE_LABEL_MIN_ZOOM ? { label: truncateLabel(art.title) } : {})}
                    />
                  ) : articleIcons[art.pageid] && articleIcons[`${art.pageid}_selected`] ? (
                    <AdvancedImageMarker
                      map={mapRef.current}
                      position={{ lat: art.lat, lng: art.lon }}
                      imgUrl={
                        articleIcons[
                        isSelectedArticleMarker
                          ? `${art.pageid}_selected`
                          : art.pageid
                        ]
                      }
                      size={art.pvSum > 5000 ? baseSize : baseSize * 0.75}
                      zIndex={art.pvSum}
                      onClick={() => selectArticle(art)}
                      extraClass={isSelectedArticleMarker ? 'selected-marker' : ''}
                      {...(mapZoom >= ARTICLE_LABEL_MIN_ZOOM ? { label: truncateLabel(art.title) } : {})}
                    />
                  ) : displayedArticles.has(art.pageid) ? (
                    // Show placeholder only for articles that have been displayed before
                    <AdvancedSvgMarker
                      map={mapRef.current}
                      position={{ lat: art.lat, lng: art.lon }}
                      svg={`
                        <svg width="${fallbackSize}" height="${fallbackSize}" viewBox="0 0 ${fallbackSize} ${fallbackSize}" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="${fallbackDotCenter}" cy="${fallbackDotCenter}" r="${fallbackDotRadius}" fill="${isSelectedArticleMarker ? '#32CD32' : '#87CEEB'}" stroke="white" stroke-width="2" opacity="0.8"/>
                        </svg>`}
                      size={fallbackSize}
                      zIndex={art.pvSum}
                      onClick={() => selectArticle(art)}
                      extraClass={isSelectedArticleMarker ? 'selected-marker' : ''}
                      {...(mapZoom >= ARTICLE_LABEL_MIN_ZOOM ? { label: truncateLabel(art.title) } : {})}
                    />
                  ) : null}
                </React.Fragment>
              );
            });
        })()}
        {/* Top-Sight markers (golden stars) */}
        {mapTopSights
          .filter((s) => s.id && Number.isFinite(s.lat) && Number.isFinite(s.lon))
          .map((s, idx) => {
            const markerId = getTopSightMarkerId(s, idx);
            const isSelected = selectedArticle?.pageid === s.id || focusedTopSightMarkerId === markerId;
            const isHiddenByOverlap = hiddenTopSightMarkerIds.has(markerId) && !isSelected;
            const topSightProminence = getTopSightProminence(s, idx, mapTopSights.length);
            const topSightZIndex = isSelected
              ? 2000000000
              : (isHiddenByOverlap ? 599000 : 600000 + Math.round(topSightProminence));
            const selectTopSight = () => {
              /* Promote this sight to the selected article so the DetailsCard switches topic */
              selectArticle({
                pageid: s.id,
                title: s.articleTitle || s.title || s.label,
                lat: s.lat,
                lon: s.lon,
                thumbnail: s.thumbnail || s.imageUrl,
                fullurl: s.link,
                pvSum: readTopSightWikiViews(s) ?? 0,
                sourceWikiLang: s.sourceWikiLang || viewportWikiLanguage,
                originalDescription: s.description,
                isTopSight: true,
              });
            };

            return (
              <React.Fragment key={`topsight-${s.id || idx}`}>
                {/* Main Top-Sight marker */}
                <AdvancedImageMarker
                  map={mapRef.current}
                  position={{ lat: s.lat, lng: s.lon }}
                  imgUrl={
                    (!isHiddenByOverlap && s.id && topSightIcons[
                      isSelected ? `${s.id}_selected` : s.id
                    ]) ||
                    (isSelected ? limeStarIcon?.url : goldStarIcon?.url) ||
                    ''
                  }
                  size={isHiddenByOverlap ? HIDDEN_TOPSIGHT_STAR_SIZE : (s.id && topSightIcons[s.id] ? TOPSIGHT_ICON_SIZE : 28)}
                  zIndex={topSightZIndex}
                  onClick={selectTopSight}
                  extraClass={[
                    isSelected ? 'selected-marker' : '',
                    isHiddenByOverlap ? 'hidden-overlap-topsight-marker' : 'top-sight-marker',
                  ].filter(Boolean).join(' ')}
                  label={!isHiddenByOverlap && mapZoom >= TOPSIGHT_LABEL_MIN_ZOOM ? truncateLabel(s.title || s.articleTitle || s.label || '') : ''}
                  markerScale={topSightMarkerVisualScale}
                />
              </React.Fragment>
            );
          })}
      </GoogleMap>

      {/* Debug geocoding comparison window disabled */}

      {/* Modern sliding details card (50% height by default) */}
      {USE_DETAILS_CARD_V2 ? (
        <DetailsCardV2
          ref={detailsCardRef}
          article={selectedArticle}
          cityName={cityName}
          viewportWikiLanguage={viewportWikiLanguage}
          userLanguageLabel={userLanguageLabel}
          userPreferredWikiLangCode={userPreferredWikiLangCode}
          onArticleSelected={selectArticle}
          onTopSightShowOnMap={showTopSightOnMap}
          onTopSightFocus={handleTopSightFocus}
          isGuestAccount={isGuestAccount}
          showPwaInstallButton={showPwaInstallButton}
          onPwaInstallClick={handlePwaInstallClick}
          onSignInClick={() => loginMachine.requestLogin('manual', { preferQuickPrompt: false })}
          onHeightChange={setDetailsCardHeightPx}
          onGoToMyLocation={goToMyLocation}
          onToggleMapType={toggleMapType}
          onOpenUserHistory={() => setShowUserHistory(true)}
          isMapCenteredOnUser={isMapCenteredOnUser}
          startNarration={narration.startNarration}
          audioEnabled={canUseAudioNarration}
          audioStatus={narration.status}
          audioTitle={narration.title}
          audioMode={narration.mode}
          onAudioPauseToggle={narration.togglePause}
          onAudioTogglePlayPause={narration.togglePlayPause}
          onAudioStop={narration.stop}
          onAudioSeek={narration.seekTo}
          audioCurrentTime={narration.currentTime}
          audioDuration={narration.duration}
        />
      ) : (
        <DetailsCard
          ref={detailsCardRef}
          article={selectedArticle}
          cityName={cityName}
          prefetchedLocationPopulation={nominatimData?.topic_population ?? null}
          viewportWikiLanguage={viewportWikiLanguage}
          userLanguageLabel={userLanguageLabel}
          userPreferredWikiLangCode={userPreferredWikiLangCode}
          onClose={() => selectArticle(null)}
          startNarration={narration.startNarration}
          onSightsReady={handleSightsReady}
          onArticleSelected={selectArticle}
          history={history}
          historyIndex={historyIndex}
          goBack={goBack}
          goForward={goForward}
          initialShowLocationSights={autoShowLocationSights}
          gpsAvailable={gps.gpsAvailable}
          isGuestAccount={isGuestAccount}
          showPwaInstallButton={showPwaInstallButton}
          onPwaInstallClick={handlePwaInstallClick}
          fromSearchfield={isFromSearchfield}
          searchFieldLocation={searchFieldLocationString}
          searchFieldRequestRequired={searchFieldRequestRequired}
          onSearchFieldRequestCompleted={() => {
            setIsFromSearchfield(false);
            setSearchFieldLocationString(null);
            setSearchFieldRequestRequired(false);
          }}
          onSignInClick={() => loginMachine.requestLogin('manual', { preferQuickPrompt: false })}
          onHeightChange={setDetailsCardHeightPx}
          onGoToMyLocation={goToMyLocation}
          onToggleMapType={toggleMapType}
          onOpenUserHistory={() => setShowUserHistory(true)}
          isMapCenteredOnUser={isMapCenteredOnUser}
          audioEnabled={canUseAudioNarration}
          audioStatus={narration.status}
          audioTitle={narration.title}
          audioMode={narration.mode}
          onAudioPauseToggle={narration.togglePause}
          onAudioTogglePlayPause={narration.togglePlayPause}
          onAudioStop={narration.stop}
          onAudioSeek={narration.seekTo}
          audioCurrentTime={narration.currentTime}
          audioDuration={narration.duration}
        />
      )}
      <SmartSignIn />
      {showSalesPrompt && (
        <SalesPromptModal onClose={() => setShowSalesPrompt(false)} />
      )}
      {showQuotaModal && <QuotaModal onClose={() => setShowQuotaModal(false)} />}

      {/* PWA Installation modals */}
      {showIOSInstallManual && (
        <IOSPWAInstallManual onClose={() => setShowIOSInstallManual(false)} />
      )}
      {showAndroidInstallManual && (
        <AndroidPWAInstallManual onClose={() => setShowAndroidInstallManual(false)} />
      )}

      {/* User History popup */}
      <UserHistoryPopup
        isOpen={showUserHistory}
        onClose={() => setShowUserHistory(false)}
        onAudioSelect={handleHistoryAudioSelect}
      />

      {/* Success notification for payment completion */}
      {showSuccessNotification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#4CAF50',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: 10000,
          fontSize: '16px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '90vw',
          textAlign: 'center'
        }}>
          <span>✅</span>
          Payment successful! Your credits have been added.
          <button
            onClick={() => setShowSuccessNotification(false)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              marginLeft: '8px',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0 4px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Sign-in success banner */}
      {showSigninSuccessBanner && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#4CAF50',
          color: 'white',
          padding: '14px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: 10000,
          fontSize: '15px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '90vw',
          textAlign: 'center'
        }}>
          <span>🎉</span>
          Sign-in completed – 3 additional free audio calls added.
          <button
            onClick={() => setShowSigninSuccessBanner(false)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              marginLeft: '8px',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0 4px'
            }}
          >
            ×
          </button>
        </div>
      )}

      <EnvironmentBanner zoomLevel={mapZoom} />
    </div>
  );
}
